import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import Thumbnail from 'component/Accommodation/Card/__partials/Aside/Thumbnail';
import Category from 'component/Accommodation/Category';
import Price from 'component/Accommodation/Pricing/Price';
import Title from 'component/Accommodation/Title';
import Loyalty from 'component/Incentive/Loyalty/Emblem';
import StrikeThroughPrice from 'component/Incentive/Tooltip/Generic/StrikeThroughPrice';
import Tooltip from 'component/Layout/Tooltip';
import { TooltipPosition } from 'component/Layout/Tooltip/types';
import Rating from 'component/User/Rating/Accommodation';

import classNames from 'classnames';

import { useActivityTracking } from 'hooks/useActivityTracking';
import { useLinkTarget } from 'hooks/useLinkTarget';

import { ReactComponent as IconInfoCircle } from 'media/svg/infoCircle.svg';

import { CommunicationContext } from 'utility/Provide/Context/PageContext';
import { updateURLParamString } from 'utility/Provide/Url';

import { SsoInformation } from 'types/legacy/backendInformation';
import { QueryParams } from 'types/Search/params';

import { SCROLL_TARGET } from '../constant';

import { ItemProps } from './types';

import styles from './Item.module.scss';

const Item = ({
    masterData,
    index,
    trackMethod,
    classListObject,
    customLinkTarget,
    showLocation = false,
    withRating = true,
    tourOperator,
    points,
    meta,
    isSponsored = false,
    withHeaderLink = false,
}: ItemProps) => {
    const { t } = useTranslation();
    const linkTarget = useLinkTarget();
    const hrefTarget = customLinkTarget ?? linkTarget;
    const { url, price, days, strikePrice, traveller } = meta;
    const namespace = useContext(CommunicationContext);
    const ssoData: SsoInformation =
        namespace.components.backendInformationFunc.getSsoData();
    const hasImage = !!masterData?.image;
    const newUrl = updateURLParamString(QueryParams.Lead, url);

    const activity = useActivityTracking();

    const onOfferLinkClick = useCallback(() => {
        trackMethod?.(
            {
                id: masterData?.id,
                traveller: traveller,
                price: price,
                isHolidayHome: masterData?.isHolidayHome,
                hotelName: masterData?.hotelName,
                hotelCategory: masterData?.hotelCategory,
                destinationLabel: masterData?.destinationLabel,
            },
            index
        );
        activity?.trackActivity(newUrl);
        namespace.components?.travelsearch?.priceRange?.saveStep3PriceForUrl(
            newUrl,
            price
        );
    }, [
        activity,
        index,
        masterData?.destinationLabel,
        masterData?.hotelCategory,
        masterData?.hotelName,
        masterData?.id,
        masterData?.isHolidayHome,
        namespace.components?.travelsearch?.priceRange,
        newUrl,
        price,
        trackMethod,
        traveller,
    ]);

    const scrollTarget = withHeaderLink ? `#${SCROLL_TARGET}` : '';

    return (
        <article
            className={classNames(styles.Wrapper, classListObject?.CardWrapper)}
        >
            <a
                href={`${newUrl}${scrollTarget}`}
                onClick={onOfferLinkClick}
                target={hrefTarget}
                rel={'noreferrer'}
                className={styles.ImageLink}
            >
                {hasImage && (
                    <>
                        <Thumbnail
                            hotelId={masterData?.id ?? 0}
                            media={masterData?.image?.url ?? ''}
                            altText={masterData?.hotelName}
                            customClass={{
                                Thumbnail: classListObject?.Thumbnail,
                            }}
                        />
                        {tourOperator?.showLogo &&
                            tourOperator?.image &&
                            tourOperator?.code && (
                                <div className={styles.TourOperatorLogoWrapper}>
                                    <img
                                        src={`${location.origin}${tourOperator.image}`}
                                        alt={tourOperator.code}
                                        height={21}
                                    />
                                </div>
                            )}
                        {!!points && (
                            <Loyalty
                                points={points}
                                isPointAccount={ssoData.isPointAccount}
                                customClass={classNames(
                                    styles.Loyalty,
                                    classListObject?.Loyalty
                                )}
                            />
                        )}
                    </>
                )}
            </a>
            <div className={classNames(styles.Hotel, classListObject?.Hotel)}>
                <a
                    href={`${newUrl}${scrollTarget}`}
                    onClick={onOfferLinkClick}
                    target={hrefTarget}
                    rel={'noreferrer'}
                >
                    {masterData?.hotelName && (
                        <Title
                            classListObject={{
                                Wrapper: classNames(
                                    styles.Title,
                                    classListObject?.TitleWrapper
                                ),
                                Text: classNames(
                                    styles.Text,
                                    classListObject?.Title
                                ),
                            }}
                            text={masterData.hotelName}
                        >
                            {!!masterData?.hotelCategoryType &&
                                !!masterData?.hotelCategory && (
                                    <Category
                                        toolTipDirection={TooltipPosition.Left}
                                        customClass={classListObject?.Category}
                                        categoryCount={masterData.hotelCategory}
                                        categoryType={
                                            masterData.hotelCategoryType
                                        }
                                    />
                                )}
                        </Title>
                    )}
                    {showLocation && masterData?.destinationLabel && (
                        <div className={styles.Location}>
                            {masterData.destinationLabel}
                        </div>
                    )}
                </a>
                <div className={styles.BottomBlock}>
                    {withRating && !!masterData?.rating && (
                        <a
                            href={`${newUrl}${scrollTarget}`}
                            onClick={onOfferLinkClick}
                            target={hrefTarget}
                            rel={'noreferrer'}
                        >
                            <Rating
                                openRatingLayer={false}
                                hotelId={masterData?.id ?? 0}
                                rating={masterData.rating}
                                classListObject={{
                                    Accommodation:
                                        classListObject?.Accommodation,
                                    RatingText: classNames(
                                        styles.RatingText,
                                        classListObject?.RatingText
                                    ),
                                }}
                            />
                        </a>
                    )}
                    {days && traveller && (
                        <div className={styles.Meta}>
                            {`${t('TRANS-day[i]', {
                                postProcess: 'interval',
                                count: days,
                            })}, ${t('TRANS-adult-short-with-count', {
                                count: traveller,
                            })}, `}
                            {t('TRANS-included')}&nbsp;{t('TRANS-flight')}
                        </div>
                    )}
                    {!!price && (
                        <div className={classListObject?.Price}>
                            <a
                                href={`${newUrl}${scrollTarget}`}
                                onClick={onOfferLinkClick}
                                target={linkTarget}
                                rel={'noreferrer'}
                            >
                                {strikePrice && (
                                    <div className={styles.StrikePriceContent}>
                                        <Price
                                            value={strikePrice.total}
                                            customClass={styles.StrikePrice}
                                            isStrikeThroughPrice
                                        />
                                    </div>
                                )}

                                <Price
                                    value={price}
                                    customClass={classListObject?.Price}
                                />
                            </a>
                            <Tooltip
                                overlayClassName={styles.PriceTooltip}
                                placement={TooltipPosition.Left}
                                stopPropagationForChildren={true}
                                align={{
                                    offset: [-5, -13],
                                }}
                                content={
                                    strikePrice ? (
                                        <StrikeThroughPrice
                                            difference={strikePrice.difference}
                                        />
                                    ) : (
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'TRANS-price-variation-hint'
                                                ),
                                            }}
                                        />
                                    )
                                }
                            >
                                <span className={styles.PriceTooltipIcon}>
                                    <IconInfoCircle />
                                </span>
                            </Tooltip>
                        </div>
                    )}
                </div>
                {isSponsored && (
                    <div className={styles.Sponsored}>
                        {t('TRANS-sponsored')}
                    </div>
                )}
            </div>
        </article>
    );
};

export default Item;
